import { SmartphoneIcon, XIcon, PlusIcon, BellIcon } from './Icons';
import React, { useState, useEffect } from 'react';
import { isIosDevice, isStandaloneApp, requestPushPermissionAndSubscribe } from '../services/pushService';
import { useClient } from '../contexts/ClientContext';

const IOSPwaPrompt: React.FC = () => {
    const { client } = useClient();
    const [isIos, setIsIos] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    const [showStandalonePushPrompt, setShowStandalonePushPrompt] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);

    useEffect(() => {
        const ios = isIosDevice();
        const standalone = isStandaloneApp();
        setIsIos(ios);
        setIsStandalone(standalone);

        if (ios && !standalone) {
            // Utilisateur sur iPhone dans Safari : afficher la bannière d'installation après 2.5 secondes si pas déjà masquée
            const dismissed = sessionStorage.getItem('astro_ios_banner_dismissed');
            if (!dismissed) {
                const timer = setTimeout(() => setShowInstallBanner(true), 2500);
                return () => clearTimeout(timer);
            }
        } else if (ios && standalone) {
            // Utilisateur sur iPhone en mode App écran d'accueil : vérifier si les notifications sont déjà autorisées
            if (typeof window !== 'undefined' && 'Notification' in window) {
                if (Notification.permission === 'default') {
                    const timer = setTimeout(() => setShowStandalonePushPrompt(true), 1500);
                    return () => clearTimeout(timer);
                } else if (Notification.permission === 'granted') {
                    // Déjà autorisé, synchroniser le token
                    requestPushPermissionAndSubscribe(client || undefined);
                }
            }
        }
    }, [client]);

    const handleDismissInstall = () => {
        sessionStorage.setItem('astro_ios_banner_dismissed', 'true');
        setShowInstallBanner(false);
    };

    const handleEnablePushOnIos = async () => {
        setIsSubscribing(true);
        try {
            await requestPushPermissionAndSubscribe(client || undefined);
            setShowStandalonePushPrompt(false);
        } catch (e) {
            console.warn("iOS Push prompt error:", e);
        } finally {
            setIsSubscribing(false);
        }
    };

    // 1. BANNIÈRE D'INSTALLATION POUR IPHONE (DANS SAFARI)
    if (showInstallBanner && isIos && !isStandalone) {
        return (
            <div className="fixed bottom-3 left-3 right-3 z-[90] max-w-md mx-auto bg-gradient-to-r from-purple-950 via-indigo-950 to-mystic-900 border border-gold-500/50 rounded-2xl p-4 shadow-2xl animate-slide-up text-white">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gold-500/20 border border-gold-500/40 flex items-center justify-center shrink-0">
                        <SmartphoneIcon className="w-5 h-5 text-gold-400" />
                    </div>
                    <div className="flex-1 text-xs">
                        <div className="flex items-center justify-between">
                            <h4 className="font-bold text-gold-300 text-sm">Activer les Pushs sur iPhone</h4>
                            <button onClick={handleDismissInstall} className="text-gray-400 hover:text-white p-1"><XIcon className="w-3.5 h-3.5" /></button>
                        </div>
                        <p className="text-gray-300 mt-1 leading-relaxed">
                            Pour recevoir les alertes de vos médiums même écran verrouillé :
                        </p>
                        <div className="mt-2.5 p-2 bg-black/40 rounded-xl border border-white/10 flex items-center justify-around gap-2 text-[11px]">
                            <div className="flex items-center gap-1.5 font-bold text-white">
                                <span className="bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono">1.</span>
                                <span>Partager</span>
                            </div>
                            <span className="text-gray-500">→</span>
                            <div className="flex items-center gap-1.5 font-bold text-gold-400">
                                <span className="bg-gold-500/20 px-1.5 py-0.5 rounded text-sm">2. +</span>
                                <span>Sur l'écran d'accueil</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 2. MODALE D'AUTORISATION PUSH (LORS DU LANCEMENT DEPUIS L'ÉCRAN D'ACCUEIL IPHONE)
    if (showStandalonePushPrompt && isIos && isStandalone) {
        return (
            <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                <div className="bg-gradient-to-b from-mystic-900 to-purple-950 border border-gold-500/50 rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-purple-600 to-gold-500 mx-auto flex items-center justify-center shadow-lg border border-white/20 mb-4 animate-bounce">
                        <BellIcon className="w-8 h-8 text-gold-300" />
                    </div>
                    <h3 className="text-lg font-serif font-bold text-white mb-2">
                        Recevoir vos révélations
                    </h3>
                    <p className="text-xs text-gray-300 mb-5 leading-relaxed">
                        Autorisez les notifications pour être alerté instantanément dès qu'un médium vous répond ou qu'une vision est prête.
                    </p>
                    <div className="flex flex-col gap-2.5">
                        <button
                            onClick={handleEnablePushOnIos}
                            disabled={isSubscribing}
                            className="w-full bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-400 hover:to-yellow-300 text-black font-extrabold py-3 rounded-xl text-sm shadow-xl transition-all hover:scale-105 active:scale-95"
                        >
                            {isSubscribing ? 'Activation en cours...' : 'Autoriser les Notifications'}
                        </button>
                        <button
                            onClick={() => setShowStandalonePushPrompt(false)}
                            className="text-xs text-gray-400 hover:text-white py-1.5"
                        >
                            Plus tard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default IOSPwaPrompt;
